import { UserAvatar } from '@/components/ui/user-avatar'
import { UserLabel } from '@/components/ui/user-label'
import { Demo, DsSection } from '../primitives'

// Inline stand-in rather than a real headshot: nobody's photo belongs in a
// checked-in reference page, and it is deliberately 3:2 so the `object-cover`
// crop back to a circle is visible.
const SAMPLE_PHOTO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80">
       <rect width="120" height="80" fill="#0f766e"/>
       <circle cx="60" cy="34" r="16" fill="#99f6e4"/>
       <path d="M28 80c0-18 14-28 32-28s32 10 32 28z" fill="#99f6e4"/>
     </svg>`
  )

// The fallback text is fixed, not derived from the circle, so each size has a
// companion `fallbackClassName`. Pairing them here is the documentation.
const SIZES = [
  { className: 'size-4', fallback: 'text-[8px]', label: 'size-4 — pipeline card' },
  { className: 'size-5', fallback: 'text-[10px]', label: 'size-5 — UserLabel default' },
  { className: 'size-6', fallback: 'text-[11px]', label: 'size-6 — activity author' },
  { className: 'size-8', fallback: 'text-xs', label: 'size-8 — default' },
  { className: 'size-12', fallback: 'text-sm', label: 'size-12 — broker widget' },
]

export function AvatarsSection() {
  return (
    <DsSection
      id="avatars"
      title="Avatars"
      intro={
        <>
          One way to picture a person. <code>UserAvatar</code> is the circle on its own;{' '}
          <code>UserLabel</code> pairs it with the name for table cells, select items and cards. Feed it the{' '}
          <code>avatar</code> field that every user-shaped API payload carries — the uploaded headshot
          (Settings → Your photo), falling back to the legacy avatar, then to initials.
        </>
      }
    >
      <Demo label="With a photo, and the initials fallback" className="flex items-center gap-6">
        <UserAvatar name="Deborah Spoonhour" src={SAMPLE_PHOTO} />
        <UserAvatar name="Deborah Spoonhour" />
        <UserAvatar name="lupita@cai.ai" />
      </Demo>

      <Demo
        label="Sizes — the fallback text does not scale with the circle; pair the two"
        className="flex items-end gap-6"
      >
        {SIZES.map((size) => (
          <div key={size.className} className="flex flex-col items-center gap-2">
            {/* Initials, not the photo: the fallback is the only part that
                needs attention as the circle shrinks. */}
            <UserAvatar
              name="Deborah Spoonhour"
              className={size.className}
              fallbackClassName={size.fallback}
            />
            <span className="text-ink-400 font-mono text-[10px]">{size.label}</span>
            <code className="text-ink-400 font-mono text-[10px]">{size.fallback}</code>
          </div>
        ))}
      </Demo>

      <Demo label="UserLabel — avatar plus name" consoleOnly className="flex flex-col items-start gap-3">
        <UserLabel name="Deborah Spoonhour" src={SAMPLE_PHOTO} />
        <UserLabel name="Lupita Ramirez" />
        {/* A bigger circle needs a bigger fallback passed alongside it. */}
        <UserLabel name="Lupita Ramirez" avatarClassName="size-7" fallbackClassName="text-xs" />
      </Demo>
    </DsSection>
  )
}
